import './EventFilters.scss'
import React, { useMemo, useState } from 'react'
import { Form, FormControlProps, Spinner } from 'react-bootstrap'
import TagsInput from 'react-tagsinput'
import { useDebouncedCallback } from 'use-debounce'
import { MultiSelectDropdown, MultiSelectOption } from '../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { useGetIntegrationEntitiesQuery } from '../../../store/integrations/integrations'
import { IntegrationServiceId } from '../../../common/integrations'
import { toSeconds } from '../../../common/util'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetMeQuery } from '../../../store/user/user'
import { EventFilterConditionOperatorType, EventFilterConditionType } from '../../../models/EventFilter'
import { SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { EventFilterConditionEdit } from './EventFilters'
import { ConditionRow } from '../../../components/ConditionRow/ConditionRow'
import { useSalesforceEventEntityOptions } from './useSalesforceEventEntityOptions'

const textualConditionOperatorOptions: SelectOption[] = [
  { id: 'equals', label: 'equals' },
  { id: 'not_equals', label: 'not equals' },
  { id: 'contains', label: 'contains' },
  { id: 'not_contains', label: 'not contains' },
  { id: 'starts_with', label: 'starts with' },
  { id: 'not_starts_with', label: 'not starts with' },
  { id: 'ends_with', label: 'ends with' },
  { id: 'not_ends_with', label: 'not ends with' },
]

const hostConditionOperatorOptions: SelectOption[] = [{ id: 'me', label: 'is me' }, { id: 'not_me', label: 'is not me' }, ...textualConditionOperatorOptions]

const dropdownConditionOperatorOptions: SelectOption[] = [
  { id: 'equals', label: 'equals' },
  { id: 'not_equals', label: 'not equals' },
]

const numericConditionOperatorOptions: SelectOption[] = [
  { id: 'equals', label: 'equals' },
  { id: 'not_equals', label: 'not equals' },
  { id: 'less_than', label: 'less than' },
  { id: 'not_less_than', label: 'not less than' },
  { id: 'greater_than', label: 'greater than' },
  { id: 'not_greater_than', label: 'not greater than' },
]

interface EntityOptionsDropdownProps {
  name: string
  options: MultiSelectOption[]
  isFetching: boolean
  value: string[] | null
  onChange: (value: string[]) => void
  disabled: boolean
}

const EntityOptionsDropdown: React.FC<EntityOptionsDropdownProps> = ({ name, options, isFetching, value, onChange, disabled }) => {
  if (isFetching) {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner size='sm' />
      </div>
    )
  }

  if (options.length === 0) {
    return <div>No options found</div>
  }

  return <MultiSelectDropdown name={name} onSelectedChanged={onChange} options={options} initialSelectedOptions={value ?? []} disabled={disabled} />
}

interface IntegrationEntityInputProps {
  accountId: string
  connector: IntegrationServiceId
  entityType: 'event_categories' | 'categories' | 'activity_codes'
  value: string[] | null
  onChange: (value: string[]) => void
  disabled: boolean
}

const IntegrationEntityInput: React.FC<IntegrationEntityInputProps> = ({ accountId, connector, entityType, value, onChange, disabled }) => {
  const { data, isFetching } = useGetIntegrationEntitiesQuery({ accountId, connector, entityType })

  const options: MultiSelectOption[] = useMemo(() => {
    if (!data || !data.data) {
      return []
    }

    const list = 'eventCategories' in data.data ? data.data.eventCategories : 'categories' in data.data ? data.data.categories : data.data.activityCodes

    return list.flatMap((e) => (e.name ? [{ id: e.id.toString(), label: e.name }] : []))
  }, [data])

  return <EntityOptionsDropdown name={`${entityType}_select`} options={options} isFetching={isFetching} value={value} onChange={onChange} disabled={disabled} />
}

interface DurationInputProps {
  value: string[] | null
  onChange: (value: string[]) => void
  disabled: boolean
}

const DurationInput: React.FC<DurationInputProps> = ({ value, onChange, disabled }) => {
  const [inputValue, setInputValue] = useState<string>(() => {
    const [seconds] = value ?? [0]
    const secondsValue = typeof seconds === 'number' ? seconds : parseFloat(seconds)
    return isNaN(secondsValue) ? '' : (secondsValue / 3600).toFixed(2)
  })

  const handleDurationChange: FormControlProps['onChange'] = (event) => {
    const newValue = event.currentTarget.value
    setInputValue(newValue)

    // If the input has been cleared, treat it as no value rather than "NaN"
    if (newValue === '') {
      onChange([])
      return
    }

    const hours = parseFloat(newValue)
    if (Number.isNaN(hours)) {
      // Invalid numeric input: do not propagate a NaN value
      return
    }

    const seconds = toSeconds(hours)
    onChange([seconds.toString()])
  }

  return (
    <div className='d-flex flex-column'>
      <div className='d-flex gap-2 align-items-center'>
        <Form.Control type='number' value={inputValue} min={0} onChange={handleDurationChange} disabled={disabled} />
        <span className='text-nowrap zocks-text-gray-600'>hours</span>
      </div>
    </div>
  )
}

export type EventFilterConditionRowProps = {
  conditionId: string
  condition: EventFilterConditionEdit
  save: (condition: EventFilterConditionEdit) => void
  remove: () => void
}

export const EventFilterConditionRow: React.FC<EventFilterConditionRowProps> = ({ conditionId, condition, save, remove }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: userData } = useGetMeQuery({ accountId: accountId })

  const integrations = useMemo(() => {
    const userIntegrations = userData?.integrations ?? []
    return {
      google: userIntegrations.some((i) => i.serviceId === IntegrationServiceId.GOOGLE_CALENDAR),
      outlook: userIntegrations.some((i) => i.serviceId === IntegrationServiceId.OUTLOOK_CALENDAR),
      exchange: userIntegrations.some((i) => i.serviceId === IntegrationServiceId.EXCHANGE_CALENDAR),
      wealthbox: userIntegrations.some((i) => i.serviceId === IntegrationServiceId.WEALTHBOX),
      redtail: userIntegrations.some((i) => i.serviceId === IntegrationServiceId.REDTAIL),
      salesforce: userIntegrations.some((i) => i.serviceId === IntegrationServiceId.SALESFORCE),
    }
  }, [userData])

  const salesforceEventTypes = useSalesforceEventEntityOptions(accountId, 'event_types', integrations.salesforce)
  const salesforceEventSubtypes = useSalesforceEventEntityOptions(accountId, 'event_subtypes', integrations.salesforce)

  const conditionTypeOptions: SelectOption[] = useMemo(() => {
    const opts = [
      { id: 'source', label: 'Source' },
      { id: 'service_id', label: 'Meeting platform' },
      { id: 'host', label: 'Host' },
      { id: 'title', label: 'Title' },
      { id: 'participant_count', label: 'Number of participants' },
      { id: 'duration', label: 'Duration' },
    ]
    if (integrations.wealthbox) {
      opts.push({ id: 'wealthbox_event_type', label: 'Wealthbox event type' })
    }
    if (integrations.redtail) {
      opts.push({ id: 'redtail_type', label: 'Redtail type' }, { id: 'redtail_category', label: 'Redtail category' })
    }
    if (salesforceEventTypes.options.length > 0 || condition.type === 'salesforce_event_type') {
      opts.push({ id: 'salesforce_event_type', label: 'Salesforce type' })
    }
    if (salesforceEventSubtypes.options.length > 0 || condition.type === 'salesforce_event_subtype') {
      opts.push({ id: 'salesforce_event_subtype', label: 'Salesforce subtype' })
    }
    return opts
  }, [integrations, salesforceEventTypes.options, salesforceEventSubtypes.options, condition.type])

  const getOperatorOptions = (): SelectOption[] => {
    switch (condition.type) {
      case 'host':
        return hostConditionOperatorOptions
      case 'participant_count':
      case 'duration':
        return numericConditionOperatorOptions
      case 'title':
        return textualConditionOperatorOptions
      default:
        return dropdownConditionOperatorOptions
    }
  }

  const saveValue = useDebouncedCallback((value: string[]) => {
    save({ ...condition, value })
  }, 400)

  const renderValueComponent = (): React.ReactNode => {
    if (condition.type === 'source') {
      const sourceOptions: MultiSelectOption[] = [{ id: 'email', label: 'Email' }]
      if (integrations.google) {
        sourceOptions.push({ id: IntegrationServiceId.GOOGLE_CALENDAR, label: 'Google Calendar' })
      }
      if (integrations.outlook) {
        sourceOptions.push({ id: IntegrationServiceId.OUTLOOK_CALENDAR, label: 'Outlook Calendar' })
      }
      if (integrations.exchange) {
        sourceOptions.push({ id: IntegrationServiceId.EXCHANGE_CALENDAR, label: 'Exchange Calendar' })
      }
      if (integrations.wealthbox) {
        sourceOptions.push({ id: IntegrationServiceId.WEALTHBOX, label: 'Wealthbox' })
      }
      if (integrations.redtail) {
        sourceOptions.push({ id: IntegrationServiceId.REDTAIL, label: 'Redtail' })
      }
      if (integrations.salesforce) {
        sourceOptions.push({ id: IntegrationServiceId.SALESFORCE, label: 'Salesforce' })
      }

      return (
        <MultiSelectDropdown
          name={`source_${conditionId}`}
          onSelectedChanged={saveValue}
          options={sourceOptions}
          initialSelectedOptions={condition.value ?? []}
          disabled={condition.enforced}
        />
      )
    }

    if (condition.type === 'service_id') {
      const serviceOptions: MultiSelectOption[] = [
        { id: IntegrationServiceId.ZOOM, label: 'Zoom' },
        { id: IntegrationServiceId.MEET, label: 'Google Meet' },
        { id: IntegrationServiceId.TEAMS, label: 'Microsoft Teams' },
        { id: IntegrationServiceId.WEBEX, label: 'Webex' },
        { id: 'null', label: 'In-person & Phone' },
      ]
      return (
        <MultiSelectDropdown
          name={`service_id_${conditionId}`}
          onSelectedChanged={(values) => saveValue(values.map((value) => (value === 'null' ? (null as unknown as string) : value)))}
          options={serviceOptions}
          initialSelectedOptions={condition?.value?.map((i) => i ?? 'null') ?? []}
          disabled={condition.enforced}
        />
      )
    }

    if (condition.type === 'wealthbox_event_type') {
      return (
        <IntegrationEntityInput
          accountId={accountId}
          connector={IntegrationServiceId.WEALTHBOX}
          entityType='event_categories'
          value={condition.value}
          onChange={saveValue}
          disabled={!!condition.enforced}
        />
      )
    }

    if (condition.type === 'redtail_category') {
      return (
        <IntegrationEntityInput
          accountId={accountId}
          connector={IntegrationServiceId.REDTAIL}
          entityType='categories'
          value={condition.value}
          onChange={saveValue}
          disabled={!!condition.enforced}
        />
      )
    }

    if (condition.type === 'redtail_type') {
      return (
        <IntegrationEntityInput
          accountId={accountId}
          connector={IntegrationServiceId.REDTAIL}
          entityType='activity_codes'
          value={condition.value}
          onChange={saveValue}
          disabled={!!condition.enforced}
        />
      )
    }

    if (condition.type === 'salesforce_event_type') {
      return (
        <EntityOptionsDropdown
          name='salesforce_event_type_select'
          options={salesforceEventTypes.options}
          isFetching={salesforceEventTypes.isFetching}
          value={condition.value}
          onChange={saveValue}
          disabled={!!condition.enforced}
        />
      )
    }

    if (condition.type === 'salesforce_event_subtype') {
      return (
        <EntityOptionsDropdown
          name='salesforce_event_subtype_select'
          options={salesforceEventSubtypes.options}
          isFetching={salesforceEventSubtypes.isFetching}
          value={condition.value}
          onChange={saveValue}
          disabled={!!condition.enforced}
        />
      )
    }

    if (condition.type === 'participant_count') {
      const numericValue = condition.value && condition.value.length > 0 ? condition.value[0] : ''
      return <Form.Control type='number' defaultValue={numericValue} min={0} onBlur={(e) => saveValue([e.currentTarget.value])} disabled={condition.enforced} />
    }

    if (condition.type === 'duration') {
      return <DurationInput value={condition.value} onChange={saveValue} disabled={!!condition.enforced} />
    }

    if (condition.type === 'host' && (condition.operator === 'me' || condition.operator === 'not_me')) {
      return null
    }

    return (
      <TagsInput
        value={condition.value ?? []}
        onlyUnique={true}
        addOnBlur={true}
        inputProps={{ placeholder: 'New value' }}
        onChange={(value) => saveValue(value)}
        disabled={condition.enforced}
      />
    )
  }

  const handleTypeChange = (typeId: string) => {
    const type = typeId as EventFilterConditionType
    save({ ...condition, type, value: null, operator: null })
  }

  const handleOperatorChange = (operatorId: string) => {
    const operator = operatorId as EventFilterConditionOperatorType
    save({ ...condition, operator })
  }

  return (
    <ConditionRow
      selectedLeftValue={condition.type}
      selectedOperator={condition.operator}
      isEnforced={!!condition.enforced}
      leftValueOptions={conditionTypeOptions}
      operatorOptions={getOperatorOptions()}
      rightValueComponent={renderValueComponent()}
      onLeftValueChange={handleTypeChange}
      onOperatorChange={handleOperatorChange}
      onRemove={remove}
    />
  )
}
